import { guid, UTC, TimeSpan, TimestampRange, isNullOrUndefined } from 'rewire-common';
import { property, DataSignal } from 'rewire-core';
import deepequals from 'fast-deep-equal';
import { INameEntity, ITimecardReason, ITimecard, IJob, IGroup, IReason } from '../../DataTypes';
import { roundTo } from '../../utility/DataConverters';
import { localTimeZoneUTCNow } from '../../utility/DateUtils';
import { combine } from './combine';
import { subtract } from './subtract';

export function _equals(value1: any, value2: any): boolean {
  const isNull1 = value1 === undefined || value1 === null;
  const isNull2 = value2 === undefined || value2 === null;
  if (isNull1 && isNull2) return true;
  if (isNull1 || isNull2) return false;
  if (value1 === value2) return true;
  if (value1.equals) return value1.equals(value2);

  return deepequals(value1, value2);
}

export function toHours(earnedDate: UTC, timestamp: UTC) {
  return timestamp.subtract(earnedDate, TimeSpan.hours, 4);
}

export function toTimestamp(earnedDate: UTC, hours: number) {
  return earnedDate.add(hours, TimeSpan.hours, 4);
}

function assignEntry(dest: TimecardEntry, src: TimecardEntry, properties: (keyof TimecardEntry)[]) {
  for (const key of properties) {
    (dest as any)[key] = src[key];
  }
}

export class TimecardEntryBase {
  protected _units!: number;
  group!: INameEntity;
  job!: IJob;
  shift!: number;
  template?: INameEntity;
  reason?: ITimecardReason;
  overtimeReason?: ITimecardReason;
  employee?: INameEntity;
  tags?: string[];
  occupation?: INameEntity;
  rate?: number;
  factor?: number;
  amount?: number;
  accountCode!: string;
  fields?: any;

  public constructor(
    public id: string,
    data?: Partial<TimecardEntry>,
  ) {
    this.assign(data);
  }

  public get isEmpty() {
    return false;
  }

  public assign(entry?: Partial<TimecardEntryBase>) {
    if (!entry) return;
    Object.assign(this, entry);
  }

  public static get properties() {
    return [
      'group',
      'job',
      'shift',
      'template',
      'reason',
      'employee',
      'tags',
      'overtimeReason',
      'occupation',
      'rate',
      'factor',
      'amount',
      'accountCode',
      'fields',
    ];
  }

  public get units() {
    return this._units;
  }
  public set units(value: number) {
    this._units = value;
  }

  clone(): TimecardEntryBase {
    const tce = new TimecardEntryBase(guid());
    for (const key of TimecardEntryBase.properties) {
      const value = (this as any)[key];
      if (value === undefined) {
        delete (tce as any)[key];
      } else {
        (tce as any)[key] = value;
      }
    }
    return tce;
  }

  static create(id: string, _start: number, _end: number, units?: number, data?: Partial<TimecardEntry>, _isEmpty = false) {
    return new TimecardEntryBase(id, data);
  }
}

interface IRange {
  start: number;
  end: number;
}
function intersection(r1: IRange, r2: IRange): IRange | undefined {
  if (r1.start >= r2.end || r1.end <= r2.start) return undefined;
  let { start, end } = r1;
  if (r2.start > r1.start) start = r2.start;
  if (r2.end < r1.end) end = r2.end;
  return { start, end };
}

function intersects(r1: IRange, r2: IRange): boolean {
  return !(r1.start >= r2.end || r1.end <= r2.start);
}

function contains(r1: IRange, r2: IRange): boolean {
  return r2.end <= r1.end && r2.start >= r1.start;
}

export class TimecardEntry extends TimecardEntryBase {
  private _start: number;
  private _end: number;
  private _isEmpty: boolean;

  private constructor(
    public id: string,
    isEmpty: boolean,
    start: number,
    end: number,
    units?: number,
    data?: Partial<TimecardEntry>,
  ) {
    super(id, data);
    this._start = start;
    if (end === undefined) throw new Error('must have an end');
    this._end = end;
    this._units = units ?? this._end - this._start;
    this._isEmpty = isEmpty;
  }

  public assign(entry?: Partial<TimecardEntry>) {
    if (!entry) return;
    Object.assign(this, entry);
  }

  public get isEmpty() {
    return this._isEmpty;
  }

  public setEmpty(defaultTimecard: TimecardEntry) {
    this.id = guid();
    this._isEmpty = true;
    if (defaultTimecard !== this) {
      for (const key of TimecardEntryBase.properties as (keyof TimecardEntry)[]) {
        const value = defaultTimecard[key];
        if (value !== undefined) {
          (this as any)[key] = value;
        } else {
          delete (this as any)[key];
        }
      }
    }
    return this;
  }

  public setRangeAndUnits(start: number, end: number, units: number) {
    this._start = start;
    this._end = end;
    this._units = units;
  }

  public get units() {
    return this._units;
  }
  public set units(value: number) {
    this._units = value;
    this._end = this._start + value;
  }

  public get start() {
    return this._start;
  }
  public set start(value: number) {
    this._start = value;
    this._units = this._end - this._start;
  }

  public get end() {
    return this._end;
  }
  public set end(value: number) {
    this._end = value;
    this._units = this._end - this._start;
  }

  merge(previous: TimecardEntry) {
    if (previous.end === this.start) {
      this.start = previous.start;
      return true;
    }

    return false;
  }

  clone(start?: number, end?: number): TimecardEntry {
    const tce = new TimecardEntry(guid(), this._isEmpty, start ?? this._start, end ?? this._end, undefined);
    for (const key of TimecardEntryBase.properties) {
      const value = (this as any)[key];
      if (value === undefined) {
        delete (tce as any)[key];
      } else {
        (tce as any)[key] = value;
      }
    }
    return tce;
  }

  static create(id: string, start: number, end: number, units: undefined | number, data?: Partial<TimecardEntry>, isEmpty = false) {
    return new TimecardEntry(id, isEmpty, start, end, units, data);
  }

  static createEmpty(start: number, end: number, data?: Partial<TimecardEntry>) {
    return TimecardEntry.create(guid(), start, end, undefined, data, true);
  }
}

export abstract class TimecardEntriesBase {
  private _entries: TimecardEntry[] = [];
  private _earnedDate: UTC;
  private _defaultEmptyTimecard: DataSignal<TimecardEntry>;
  private _premiums!: (TimecardEntry | TimecardEntryBase)[];
  private _absences!: TimecardEntry[];
  private _entriesWithoutTimes!: TimecardEntryBase[];
  public autoCreateVacancies = true;
  public showPremiums = false;
  private _showBreaks = true;
  public breakdownMode = false;
  private _breaks: TimecardEntry[] = [];
  protected constructor() {
    this._earnedDate = localTimeZoneUTCNow().startOfDay();
    this._defaultEmptyTimecard = property({} as TimecardEntry);
  }

  protected isEntryWithTimes(timecard: ITimecard | TimecardEntryBase): timecard is TimecardEntry {
    return !isNullOrUndefined((timecard as TimecardEntry).start);
  }

  protected isPremium(timecard: ITimecard | TimecardEntryBase) {
    return !(timecard as TimecardEntry).isEmpty && timecard?.reason?.category === 'premium';
  }

  protected isWorked(timecard: ITimecard | TimecardEntry) {
    if (typeof timecard.start !== 'number' && !(timecard as ITimecard).effective) return false;
    const category = timecard?.reason?.category;
    return !(timecard as TimecardEntry).isEmpty && category && category === 'worked';
  }

  protected isWorkedOrAbsenceWithoutTimes(timecard: ITimecard | TimecardEntryBase) {
    const t: any = timecard;
    if (typeof t.start === 'number' || t.effective) return false;
    const category = t?.reason?.category;
    return !(t as TimecardEntry).isEmpty && category && (category === 'worked' || category === 'absence');
  }

  protected isAbsence(timecard: ITimecard | TimecardEntry) {
    if (typeof timecard.start !== 'number' && !(timecard as ITimecard).effective) return false;
    const category = timecard?.reason?.category;
    return !(timecard as TimecardEntry).isEmpty && category && category === 'absence';
  }

  protected isBreak(timecard: ITimecard | TimecardEntry | TimecardEntryBase) {
    if (typeof (timecard as ITimecard).start !== 'number' && !(timecard as unknown as ITimecard).effective) return false;
    return !(timecard as TimecardEntry).isEmpty && timecard?.reason?.category === 'break';
  }

  protected isEmpty(timecard: ITimecard | TimecardEntry) {
    return (timecard as TimecardEntry).isEmpty;
  }

  protected isOther(timecard: ITimecard | TimecardEntryBase) {
    const t: any = timecard;
    if (typeof t.start === 'number' || t.effective) {
      return !(timecard as TimecardEntry).isEmpty && timecard?.reason?.category === 'other';
    }
    return !(timecard as TimecardEntry).isEmpty && timecard?.reason?.category === 'other';
  }

  protected empty(entry: TimecardEntry, defaultTimecard?: TimecardEntry) {
    return entry.setEmpty(defaultTimecard ?? entry);
  }

  get earnedDate() {
    return this._earnedDate;
  }

  get totalUnits() {
    return roundTo(
      this._entries.reduce((prev, curr) => prev + (this.isWorked(curr) && curr.employee ? curr.units : 0), 0) +
        this._entriesWithoutTimes.reduce(
          (prev, curr) => prev + (['absence', 'worked'].includes(curr.reason?.category ?? '') && curr.employee ? curr.units : 0),
          0,
        ),
      3,
    );
  }

  categoryRank(entry: TimecardEntry) {
    if (entry.isEmpty || this.isWorked(entry)) return 1;
    switch (entry.reason?.category) {
      case 'worked':
        return 1.5;
      case 'break':
        return 1;
      case 'absence':
        return 2;
      case 'premium':
        return 3;
      default:
        return 4;
    }
  }

  sortFn = (entry1: TimecardEntry, entry2: TimecardEntry) => {
    if (entry2.start && entry2.start === Number.NEGATIVE_INFINITY) return 1;
    if (entry1.start === Number.NEGATIVE_INFINITY) return -1;
    // if (entry2.end === Number.POSITIVE_INFINITY) return -1;
    // if (entry1.end === Number.POSITIVE_INFINITY) return 1;
    const v = this.categoryRank(entry1) - this.categoryRank(entry2);
    if (v !== 0) return v;
    if (entry1.start && entry2.start) return entry1.start - entry2.start;
    return (entry1.start ?? Number.NEGATIVE_INFINITY) - (entry2.start ?? Number.NEGATIVE_INFINITY);
  };

  public get entries(): TimecardEntry[] {
    const entries = [...this._entries, ...this._absences];
    if (this.showPremiums) entries.push(...(this._premiums as TimecardEntry[]));
    entries.push(...(this._entriesWithoutTimes as TimecardEntry[]));
    entries.sort(this.sortFn);
    return entries;
  }

  clear() {
    this.set(this.createTimecard(this.defaultEmptyTimecard), []);
  }

  private combine(populateDefaults = false, forceCombine = false) {
    if (forceCombine || !this.breakdownMode) {
      this._entries = combine(this._entries, this.groupingKey, this.sortFn);
    }
    this._entries.sort(this.sortFn);
    if (populateDefaults) this.setDefaultsForEmptyRows();
  }

  private recalculateBreaks(forceCombine = false) {
    const changed = this.updateBreaks();
    if (!this._showBreaks) {
      this._entries = this.rollup(this._entries);
      this._absences = this.rollup(this._absences);
      this._entries = this._entries.filter((t) => !this.isBreak(t));
    }
    this.combine(true, forceCombine);
    return changed;
  }

  public get showBreaks() {
    return this._showBreaks;
  }
  public setShowBreaks(show: boolean) {
    this._showBreaks = show;
    return this.recalculateBreaks();
  }

  abstract groupingKey(timecard: TimecardEntry): string;

  public get defaultEmptyTimecard() {
    return this._defaultEmptyTimecard();
  }

  public set(defaultEmptyTimecard: ITimecard, entries: ITimecard[]) {
    // split the entries into premiums, breaks and worked entries.
    // clone all of the breaks so we can preserve them and return them back to their original space (breaks are fixed and immutable)
    this._earnedDate = defaultEmptyTimecard.earnedDate;
    this._defaultEmptyTimecard(
      this.createTimecardEntry({ ...defaultEmptyTimecard, effective: new TimestampRange(UTC.MinValue, UTC.MaxValue) }),
    );
    this._entries = [TimecardEntry.createEmpty(Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY)];
    this._entriesWithoutTimes = [];
    this._premiums = [];
    this._absences = [];
    this._breaks = [];
    for (const entry of entries) {
      if (this.isOther(entry)) continue;

      if (this.isWorkedOrAbsenceWithoutTimes(entry)) {
        this._entriesWithoutTimes.push(this.createWithoutTimes(entry));
        continue;
      }

      if (this.isPremium(entry)) {
        const timecardEntry = entry.effective ? this.createTimecardEntry(entry) : this.createWithoutTimes(entry);
        this._premiums.push(timecardEntry);
        continue;
      }

      if (!entry.effective) continue;
      const timecardEntry = this.createTimecardEntry(entry);
      if (this.isAbsence(entry)) {
        this._absences.push(timecardEntry);
        continue;
      }

      if (this.isBreak(entry)) {
        this._breaks.push(timecardEntry);
      }

      this._entries = subtract(this._entries, timecardEntry);
      this._entries.push(timecardEntry);
    }
    this.recalculateBreaks(true);
  }

  rollup(entries: TimecardEntry[], except: TimecardEntry | undefined = undefined) {
    if (!entries || entries.length === 0 || this._breaks.length === 0) return entries;
    const result: TimecardEntry[] = [];
    entries.sort(this.sortFn);
    for (const current of entries) {
      if (this.isEmpty(current)) {
        result.push(current);
        continue;
      }
      if (except && current.id === except.id) {
        result.push(current);
        continue;
      }
      const st = this._breaks.find((b) => b.end === current.start)?.start ?? current.start;
      const currentGrouping = this.groupingKey(current);
      const ea = result.find(
        (existing) => existing.end === st && !this.isEmpty(existing) && this.groupingKey(existing) === currentGrouping,
      );
      if (!ea) {
        result.push(current);
        continue;
      }
      const units = ea.units + current.units;
      const start = Math.min(ea.start, current.start);
      const end = Math.max(ea.end, current.end);
      ea.setRangeAndUnits(start, end, units);
    }
    return result;
  }

  setDefaultsForEmptyRows() {
    for (let index = 0; index < this._entries.length; index++) {
      const element = this._entries[index];
      if (!element.isEmpty) continue;
      const previous = this._entries[index - 1];
      const next = this._entries[index + 1];

      if (previous && !previous.isEmpty && this.isWorked(previous)) {
        this.empty(element, previous);
      } else if (next && !next.isEmpty && this.isWorked(next)) {
        this.empty(element, next);
      } else {
        this.empty(element, this.defaultEmptyTimecard);
      }
    }
  }

  public get(): ITimecard[] {
    // if we are hiding the breaks merge them back in for saving
    let entries: TimecardEntry[];
    let absences = this._absences.map((e) => e.clone());
    if (!this._showBreaks) {
      entries = this._entries.reduce((prev, curr) => {
        if (!this.isEmpty(curr)) prev.push(curr.clone());
        return prev;
      }, [] as TimecardEntry[]);

      for (const b of this._breaks) {
        entries = subtract(entries, b);
        entries.push(b);
        absences = subtract(absences, b);
      }
    } else {
      entries = [...this._entries.filter((te) => !te.isEmpty)];
    }

    entries.push(...absences);
    entries.push(...(this._premiums as TimecardEntry[]));
    entries.push(...(this._entriesWithoutTimes as TimecardEntry[]));
    return entries.map(this.createTimecard);
  }

  protected createTimecardEntry(timecard: ITimecard): TimecardEntry {
    if (!timecard.effective) throw new Error('you cannot create a timecard without an effective range');
    return TimecardEntry.create(
      timecard.id,
      toHours(this._earnedDate, timecard.effective.start),
      toHours(this._earnedDate, timecard.effective.end),
      timecard.units,
      timecard as unknown as TimecardEntry,
    );
  }

  protected createWithoutTimes(timecard: ITimecard): TimecardEntryBase {
    return new TimecardEntryBase(timecard.id, timecard as unknown as TimecardEntry);
  }

  protected createTimecard = (entry: TimecardEntry): ITimecard => {
    const timecard: Partial<ITimecard> = { id: entry.id };
    for (const key of TimecardEntryBase.properties as (keyof TimecardEntry & keyof ITimecard)[]) {
      timecard[key] = entry[key];
    }
    if (typeof entry.start === 'number') {
      timecard.effective = new TimestampRange(toTimestamp(this._earnedDate, entry.start), toTimestamp(this._earnedDate, entry.end));
    }
    const group = entry.group ?? this.defaultEmptyTimecard.group;
    if (!group) throw new Error('group undefined');
    timecard.earnedDate = this._earnedDate;
    timecard.units = entry.units;
    timecard.group = group as IGroup;
    timecard.occupation = entry.occupation ?? this.defaultEmptyTimecard.occupation;
    timecard.job = entry.job ?? this.defaultEmptyTimecard.job;
    timecard.shift = entry.shift;
    timecard.employee = entry.employee;
    timecard.tags = entry.tags;
    timecard.start = timecard.effective?.start;
    timecard.end = timecard.effective?.end;
    timecard.rate = typeof entry.rate === 'string' ? Number.parseFloat(entry.rate) : entry.rate;

    if (timecard.units && timecard.rate && timecard.factor)
      timecard.amount = roundTo(timecard.units * timecard.rate * timecard.factor, 5);
    return timecard as ITimecard;
  };

  public equals(entry1: TimecardEntry | TimecardEntryBase, entry2: TimecardEntry | TimecardEntryBase) {
    const timecardEntry1 = this.isEntryWithTimes(entry1) ? entry1 : undefined;
    const timecardEntry2 = this.isEntryWithTimes(entry2) ? entry2 : undefined;
    if (!timecardEntry1 !== !timecardEntry2) return false;
    if (
      timecardEntry1 &&
      timecardEntry2 &&
      (!_equals(timecardEntry1.start, timecardEntry2.start) || !_equals(timecardEntry1.end, timecardEntry2.end))
    ) {
      return false;
    }

    return (
      _equals(entry1.isEmpty, entry2.isEmpty) &&
      _equals(entry1.rate, entry2.rate) &&
      _equals(entry1.factor, entry2.factor) &&
      _equals(entry1.amount, entry2.amount) &&
      _equals(entry1.units, entry2.units) &&
      _equals(entry1.accountCode, entry2.accountCode) &&
      _equals(entry1.group?.id, entry2.group?.id) &&
      _equals(entry1.template?.id, entry2.template?.id) &&
      _equals(entry1.reason?.id, entry2.reason?.id) &&
      _equals(entry1.overtimeReason?.id, entry2.overtimeReason?.id) &&
      _equals(entry1.tags, entry2.tags) &&
      _equals(entry1.occupation?.id, entry2.occupation?.id) &&
      _equals(entry1.employee?.id, entry2.employee?.id) &&
      _equals(entry1.fields, entry2.fields)
    );
  }

  private merge<T extends TimecardEntryBase>(dest: T, src: T) {
    for (const key of TimecardEntryBase.properties as (keyof T)[]) {
      dest[key] = src[key];
    }
  }

  private editNonWorkedEntry<T extends TimecardEntryBase>(entries: T[], entry: T) {
    if (this.breakdownMode) {
      const existingEntry = entries.find((e) => e.id === entry.id);
      if (existingEntry) {
        const existingUnits = existingEntry.units;
        const units = entry.units;
        if (units < existingUnits) {
          entry.units = existingUnits - units;
          entry.id = guid();
          entries.splice(entries.indexOf(existingEntry) + 1, 0, entry);
        }

        this.merge(existingEntry, entry);
        existingEntry.units = units;
      } else {
        this.removeEntry(entries, entry.id);
        entries.push(entry);
      }
      return;
    }
    const existingEntry = entries.find((e) => e.id === entry.id) as TimecardEntryBase;
    if (existingEntry) {
      this.merge(existingEntry, entry);
      existingEntry.units = entry.units;
    } else {
      this.removeEntry(entries, entry.id);
      entries.push(entry);
    }
  }

  assertIsValid(entry: TimecardEntry | TimecardEntryBase) {
    if (entry.isEmpty) return;
    if (this.isOther(entry)) throw new Error('Timecards with the "other" category are not supported');
    if (this.isEntryWithTimes(entry)) {
      if (entry.start === Number.NEGATIVE_INFINITY || entry.end === Number.POSITIVE_INFINITY)
        throw new Error('You cannot add an entry that is unbounded, perhaps you are missing the start or the end time?');
      if (this.isAbsence(entry) && !entry.employee) throw new Error('You must specify an employee in order to create an absence');
    } else {
      if (!entry.units) {
        throw new Error('You must provide a value for the units field');
      }
    }

    if (entry.units <= 0 || entry.units > 1000)
      throw new Error('Invalid units have been entered, perhaps the start time >= to the end time?');
    if (!entry.group) throw new Error('the group is a required field');
    if (!entry.occupation) throw new Error('the occupation is a required field');
    if (!entry.reason) throw new Error('the reason is a required field');
  }

  protected subtractAbsences(entry: TimecardEntry) {
    const employee = entry.employee;
    const [myAbsences, other] = this._absences.reduce(
      (prev, curr) => {
        prev[curr.employee?.id === employee?.id ? 0 : 1].push(curr);
        return prev;
      },
      [[], []] as [TimecardEntry[], TimecardEntry[]],
    );
    this._absences = [...subtract(myAbsences, entry), ...other];
  }

  protected bookOff(entry: TimecardEntry) {
    this.removeEntry(this._absences, entry.id);
    const employee = entry.employee;
    this.subtractAbsences(entry);
    this._absences.push(entry.clone());
    for (const b of this._breaks) {
      this._absences = subtract(this._absences, b);
    }

    let edits: TimecardEntry[] = [];
    for (const existingEntry of this._entries) {
      if (existingEntry.employee?.id !== employee?.id || this.isAbsence(existingEntry)) continue;
      const i = intersection(entry, existingEntry);
      if (i) {
        edits.push(existingEntry.clone(i.start, i.end));
      }
    }

    // unschedule from all hidden intersecting breaks.
    const danglingBreaks: TimecardEntry[] = [];
    for (const b of this._breaks) {
      if (intersects(b, entry) && b.employee?.id === employee?.id) {
        b.employee = undefined;
        if (!this.autoCreateVacancies) {
          danglingBreaks.push(b);
        }
      }
    }

    const existingEntry = this._entries.find((e) => e.id === entry.id);
    if (existingEntry) {
      // set the original entry to be empty essentially clearing out it's old space
      this.empty(existingEntry);
    }

    if (edits.length > 0) {
      if (!this._showBreaks) {
        for (const b of this._breaks) {
          edits = subtract(edits, b);
        }
        edits = this.rollup(edits);
        this._absences = this.rollup(this._absences);
      }
      // remove all dangling breaks after rollups applied
      for (const db of danglingBreaks) {
        this._breaks = subtract(this._breaks, db);
      }
      for (const e of edits) {
        delete e.employee;
        this._entries = subtract(this._entries, e);
        if (this.autoCreateVacancies) {
          // insert the new vacancy in the hole.
          this._entries.push(e);
        }
      }
    }

    this.combine(true);
  }

  private updateBreaks() {
    let changed = false;
    for (const b of this._breaks) {
      const entryAtBreak = this._entries.find((e) => this.isWorked(e) && intersects(e, b));
      if (entryAtBreak) {
        if (b.employee?.id !== entryAtBreak.employee?.id) changed = true;
        if (this.isAbsence(entryAtBreak)) {
          b.employee = undefined;
          // do something here!!
        } else {
          assignEntry(b, entryAtBreak, ['employee', 'occupation', 'group', 'template', 'job']);
        }
      }

      if (this._showBreaks) {
        this._entries = subtract(this._entries, b);
        this._absences = subtract(this._absences, b);
        this._entries.push(b);
      }
    }

    return changed;
  }

  public computeUnits(reason: IReason | undefined, start: number, end: number): number {
    const entry = { start, end, units: end - start };
    if (reason?.category !== 'worked') return roundTo(entry.units, 4);
    const entryAtBreak = this._breaks.find((b) => intersects(b, entry));
    if (!entryAtBreak) {
      return roundTo(entry.units, 5);
    }
    return roundTo(end - start - entryAtBreak.units, 5);
  }

  public computeStartValue(reason: IReason | undefined, end: number, units: number): number {
    const entry = { start: end - units, end, units };
    if (reason?.category !== 'worked') return roundTo(entry.start, 5);
    const entryAtBreak = this._breaks.find((b) => intersects(b, entry));
    if (!entryAtBreak) {
      return roundTo(entry.start, 5);
    }
    return roundTo(end - units - entryAtBreak.units, 5);
  }

  public computeEndValue(reason: IReason | undefined, start: number, units: number): number {
    const entry = { start, end: start + units, units };
    if (reason?.category !== 'worked') return roundTo(entry.end, 5);
    const entryAtBreak = this._breaks.find((b) => intersects(b, entry));
    if (!entryAtBreak) {
      return roundTo(entry.end, 5);
    }
    return roundTo(start + units + entryAtBreak.units, 5);
  }

  public edit(entry: TimecardEntry | TimecardEntryBase) {
    if (!entry) throw new Error('ugh really??');
    this.assertIsValid(entry);

    const existingEntry = this._entries.find((e) => e.id === entry.id);

    if (existingEntry && this.isBreak(existingEntry)) {
      this._breaks = subtract(this._breaks, existingEntry);
    }

    if (this.isPremium(entry)) {
      return this.editNonWorkedEntry(this._premiums, entry);
    }

    if (!this.isEntryWithTimes(entry)) {
      if (existingEntry) this.empty(existingEntry);
      return this.editNonWorkedEntry(this._entriesWithoutTimes, entry);
    }

    if (this.isAbsence(entry)) {
      this.bookOff(entry);
      return;
    }

    if (!this.isBreak(entry)) {
      // the entry is a clone of one of the managed entries with some of its properties changed (MUST BE IN THE LIST).
      if (!existingEntry) {
        if (!this.removeEntry(this._absences, entry.id)) {
          throw new Error('you cannot change a premium or any other type of entry to worked');
        }
      } else {
        // optimization: if it is equal to the existing entry than no changes so return!
        if (this.equals(existingEntry, entry)) return;

        if (this.breakdownMode) existingEntry.id = guid();
        else this.empty(existingEntry);
        // set the original entry to be empty essentially clearing out it's old space
      }

      if (this.isWorked(entry)) {
        this.subtractAbsences(entry);
      }
    } else {
      if (existingEntry) {
        this.empty(existingEntry);
      }
      this._breaks = subtract(this._breaks, entry);
      this._breaks.push(entry);
    }

    // subtract out the range of the entry making a hole to insert it into.
    this._entries = subtract(this._entries, entry);
    // insert the new entry in the hole.
    this._entries.push(entry);

    // if (this._showBreaks) {
    //   this._breaks = this._entries.filter(e => this.isBreak(e));
    // }

    // write the breaks over top of the existing entries. make sure the employe is the one working break
    if (this.breakdownMode) {
      this._entries = this.rollup(this._entries, existingEntry);
      this.updateBreaks();
      this.combine(true, false);
      return;
    }
    this.recalculateBreaks();

    // combine all of the entries that are contiguous and are mergeable.
    // this.combine(true);
  }

  public removeEntry(items: TimecardEntryBase[], id: string) {
    const idx = items.findIndex((item) => item.id === id);
    if (idx < 0) return false;
    items.splice(idx, 1);
    return true;
  }

  public remove(entry: TimecardEntry) {
    if (this.isPremium(entry)) {
      this.removeEntry(this._premiums, entry.id);
      return;
    }

    if (this.isAbsence(entry)) {
      this.removeEntry(this._absences, entry.id);
      return;
    }

    if (this.isWorkedOrAbsenceWithoutTimes(entry as TimecardEntryBase)) {
      this.removeEntry(this._entriesWithoutTimes, entry.id);
      return;
    }

    if (this.isBreak(entry)) {
      entry.reason = this.defaultEmptyTimecard.reason;
      this.removeEntry(this._breaks, entry.id);
      this.recalculateBreaks();
      return;
    }

    // the entry is a clone of one of the managned entries with some of its properties changed (MUST BE IN THE LIST).
    const existingEntry = this._entries.find((e) => e.id === entry.id);
    if (!existingEntry) throw new Error('something has gone horribly wrong');

    if (!this.showBreaks) {
      // remove all dangling breaks
      const danglingBreaks: TimecardEntry[] = [];
      for (const b of this._breaks) {
        if (contains(existingEntry, b) && existingEntry.employee?.id === b.employee?.id) {
          danglingBreaks.push(b);
        }
      }

      for (const db of danglingBreaks) {
        this._breaks = subtract(this._breaks, db);
      }
    }

    this.empty(existingEntry);
    this.updateBreaks();
    this.combine(false);
  }
}

// Timecard Entries edit employee mode timecards
// absences are considered worked as an employee cannot be absent and working at the same time and changing the absence reason is not a book off in the timecard editor
// also reasons, overtime reasons and factors are cleared when setting a row to "empty"
export class TimecardEntries extends TimecardEntriesBase {
  constructor() {
    super();
  }

  groupingKey(timecard: TimecardEntry): string {
    if (timecard.isEmpty) return '';
    const fields = timecard.fields ? JSON.stringify(timecard.fields) : '';
    const tags = timecard.tags && Array.isArray(timecard.tags) && timecard.tags.length > 0 ? JSON.stringify(timecard.tags) : '';
    return `${timecard.accountCode ?? ''}${timecard?.reason?.id ?? ''}${timecard?.overtimeReason?.id ?? ''}${
      timecard?.factor ?? ''
    }${tags}${timecard?.group?.id ?? ''}${timecard?.template?.id ?? ''}${timecard?.occupation?.id ?? ''}${fields}`;
  }

  protected empty(entry: TimecardEntry, defaultTimecard: TimecardEntry) {
    entry.setEmpty(defaultTimecard ?? entry);
    delete entry.reason;
    delete entry.overtimeReason;
    delete entry.factor;
    return entry;
  }

  protected isWorked(timecard: ITimecard | TimecardEntry) {
    if (typeof timecard.start !== 'number' && !(timecard as ITimecard).effective) return false;
    const category = timecard?.reason?.category;
    return !(timecard as TimecardEntry).isEmpty && category && (category === 'worked' || category === 'absence');
  }

  protected isAbsence(_timecard: ITimecard | TimecardEntry): boolean | undefined {
    return false;
  }
}

// Shift Entries edit timecards in job mode
// you combine only the visible shift editing properties reason and employee.
export class ShiftEntries extends TimecardEntriesBase {
  constructor(public groupingKey: (timecard: TimecardEntry) => string) {
    super();
  }

  protected empty(entry: TimecardEntry, defaultTimecard: TimecardEntry) {
    entry.setEmpty(defaultTimecard ?? entry);
    if (this.defaultEmptyTimecard.employee?.id !== 'none') {
      delete entry.employee;
    }
    return entry;
  }
}

// Employee Shift Entries edit timecards in employee mode
export class EmployeeShiftEntries extends TimecardEntriesBase {
  constructor(public groupingKey: (timecard: TimecardEntry) => string) {
    super();
  }
}
